import cn from 'classnames';
import { type CSSProperties, type FC, type Key, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { _isNil } from '@/littledash';
import { isHexColor, type TextTone, textToneForBackground } from '@/utils/Color.utils';

import styles from './GroupChip.module.scss';

export type GroupChipTextColorMode = 'auto' | TextTone;

/** HSL lightness (0–1) at or above which `auto` switches the number to $near-black. */
const DARK_TEXT_MIN_LIGHTNESS = 0.65;
/** WCAG contrast below which $white is unreadable on the chip, so `auto` uses $near-black. */
const MIN_LIGHT_TEXT_CONTRAST = 1.6;

interface GroupChipProps {
  no: number;
  color?: string | null;
  textColorMode?: GroupChipTextColorMode;
  testId?: string;
}

const resolveTextTone = (color: string | null | undefined, mode: GroupChipTextColorMode): TextTone => {
  if (mode !== 'auto') {
    return mode;
  }
  const background = isHexColor(color) ? color : styles.defaultBackgroundColor;
  return textToneForBackground(background, {
    darkTextMinLightness: DARK_TEXT_MIN_LIGHTNESS,
    lightText: styles.lightTextColor,
    minLightTextContrast: MIN_LIGHT_TEXT_CONTRAST,
  });
};

/**
 * Square chip showing a group number on the group's colour. In `auto` mode the number is $white,
 * switching to $near-black on light colours or where $white would be unreadable; `light` and `dark` force one.
 */
const GroupChip: FC<GroupChipProps> = ({ no, color, textColorMode = 'auto', testId = 'group-chip' }) => {
  const textTone = resolveTextTone(color, textColorMode);

  return (
    <span
      className={styles.chip}
      style={isHexColor(color) ? { backgroundColor: color } : undefined}
      data-test-component="GroupChip"
      data-test-element="chip"
      data-test-key={String(no)}
      data-testid={testId}
    >
      <span className={cn(styles.number, styles[textTone])}>{no}</span>
    </span>
  );
};

const cssLength = (value: string): number => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Content-box height of the table cell holding the list. The list itself is only as tall as its
 * chips, so the cell is what bounds the rows. Zero outside a cell.
 */
const availableHeight = (list: HTMLElement): number => {
  const cell = list.closest<HTMLElement>('[role="cell"], [role="gridcell"]');
  if (_isNil(cell)) {
    return 0;
  }
  const { paddingTop, paddingBottom } = getComputedStyle(cell);
  return cell.clientHeight - cssLength(paddingTop) - cssLength(paddingBottom);
};

interface GridCapacity {
  columns: number;
  rows: number;
}

export type GroupChipListMaxRows = number | 'auto';

/**
 * Column count the browser resolved for the `auto-fill` grid, and how many chip rows the
 * surrounding table cell's height allows: at least one, and no more than `maxRows` unless it is
 * `auto`. Null before the grid is laid out.
 */
const measureCapacity = (list: HTMLElement, maxRows: GroupChipListMaxRows): GridCapacity | null => {
  const { gridTemplateColumns, gridAutoRows, rowGap } = getComputedStyle(list);
  const columns = gridTemplateColumns.split(' ').filter((track) => cssLength(track) > 0).length;
  const rowHeight = cssLength(gridAutoRows);
  if (columns === 0 || rowHeight === 0) {
    return null;
  }
  const fittingRows = Math.floor((availableHeight(list) + cssLength(rowGap)) / (rowHeight + cssLength(rowGap)));
  const rows = maxRows === 'auto' ? fittingRows : Math.min(maxRows, fittingRows);
  return { columns, rows: Math.max(1, rows) };
};

const sameCapacity = (left: GridCapacity | null, right: GridCapacity | null): boolean =>
  left?.columns === right?.columns && left?.rows === right?.rows;

export interface GroupChipListItem extends GroupChipProps {
  key: Key;
}

interface GroupChipListProps {
  chips: Array<GroupChipListItem>;
  /** Upper limit on chips shown, including the `+N` chip. */
  maxChips?: number;
  /** Upper limit on chip rows, or `auto` for as many as the surrounding table cell fits. */
  maxRows?: GroupChipListMaxRows;
}

/**
 * Grid of group chips that takes the width its parent gives it, filling as many rows as the
 * surrounding table cell's height allows, optionally capped by `maxRows`. CSS lays out and clips the chips, so overflow is never
 * painted; the resolved column count then decides how many chips give way to a trailing `+N`
 * chip, which also caps the list at `maxChips`. Resize Observer callbacks run between layout and
 * paint, and `flushSync` commits the new count there, so `+N` is right on the first frame and on
 * every resize.
 */
export const GroupChipList: FC<GroupChipListProps> = ({ chips, maxChips, maxRows = 'auto' }) => {
  const listRef = useRef<HTMLSpanElement | null>(null);
  const [capacity, setCapacity] = useState<GridCapacity | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (_isNil(list)) {
      return;
    }
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const remeasure = (): void => {
      const next = measureCapacity(list, maxRows);
      setCapacity((current) => (sameCapacity(current, next) ? current : next));
    };
    const observer = new ResizeObserver(() => flushSync(remeasure));
    observer.observe(list);
    return () => observer.disconnect();
  }, [maxRows]);

  const fittingCount = _isNil(capacity) ? Infinity : capacity.columns * capacity.rows;
  const limit = Math.min(fittingCount, maxChips ?? Infinity);
  const visibleCount = chips.length > limit ? Math.max(0, limit - 1) : chips.length;
  const hiddenCount = chips.length - visibleCount;

  return (
    <span
      ref={listRef}
      className={styles.list}
      style={{ '--group-chip-rows': capacity?.rows ?? 1 } as CSSProperties}
      data-test-component="GroupChipList"
      data-test-element="container"
    >
      {chips.slice(0, visibleCount).map(({ key, ...chip }) => (
        <GroupChip key={key} {...chip} />
      ))}
      {hiddenCount > 0 && (
        <span
          className={styles.overflow}
          title={`${hiddenCount} more groups`}
          data-test-component="GroupChipList"
          data-test-element="overflow"
        >
          <span className={styles.number}>+{hiddenCount}</span>
        </span>
      )}
    </span>
  );
};

export default GroupChip;
